import useAuth from '@/libs/auth0/use-auth';
import { NEEDS_REGISTRATION_CONSENT } from '@/utils/constants';
import { queryClient, queryKeys, useQuery } from '@rhg/libs/react-query';
import { sha256 } from '@rhg/utils/string';
import { useCallback } from 'react';

interface NeedsRegistrationConsentData {
    localStorageKey: string | null;
    needsRegistrationConsent: string | null;
}

const placeholderData = {
    localStorageKey: null,
    needsRegistrationConsent: null,
};

async function getUserSha(userId: string) {
    const localStorageKey = `${NEEDS_REGISTRATION_CONSENT}-${await sha256(
        userId
    )}`;
    return localStorageKey;
}
function getQueryKey(userId?: string) {
    return [queryKeys.needsRegistrationConsent, userId];
}

export const useNeedsRegistrationConsent = () => {
    const { isAuthenticated, user } = useAuth();
    const { data, isFetched, ...otherProps } =
        useQuery<NeedsRegistrationConsentData>({
            queryKey: getQueryKey(user?.sub),
            queryFn: async () => {
                if (isAuthenticated && user?.sub) {
                    const localStorageKey = await getUserSha(user.sub);
                    const needsRegistrationConsent =
                        // eslint-disable-next-line no-restricted-syntax
                        localStorage.getItem(localStorageKey);

                    return { localStorageKey, needsRegistrationConsent };
                }
                return placeholderData;
            },
            enabled: isAuthenticated && !!user?.sub,
            staleTime: 1000 * 60 * 60 * 8,
        });

    const setNeedsRegistrationConsent = useCallback(
        async (value: boolean) => {
            if (user?.sub) {
                const localStorageKey = await getUserSha(user.sub);
                // eslint-disable-next-line no-restricted-syntax
                localStorage.setItem(localStorageKey, `${value}`);
                queryClient.setQueryData(getQueryKey(user.sub), `${value}`);
                return;
            }
            throw new Error(
                'setNeedsRegistrationConsent: hook must be fetched before it can be called'
            );
        },
        [user?.sub]
    );

    const clearNeedsRegistrationConsent = useCallback(async () => {
        if (user?.sub) {
            const localStorageKey = await getUserSha(user.sub);
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(localStorageKey);
            queryClient.setQueryData(getQueryKey(user.sub), null);
            return;
        }
        throw new Error(
            'clearNeedsRegistrationConsent: hook must be fetched before it can be called'
        );
    }, [user?.sub]);

    return {
        ...otherProps,
        data,
        isFetched,
        /** Sets the required parental consent to appear within the registration flow */
        setNeedsRegistrationConsent,
        /** Clears the registration flow requirement for the parental consent we must wait for hook isFetched to be true */
        clearNeedsRegistrationConsent,
    };
};
